import React, { useState } from "react";
import "./PolicyStudioSidebar.css";
import PolicyAdjustmentCard from "./components/PolicyAdjustmentCard";
import UpdateHistoryItem from "./components/UpdateHistoryItem";
import ActionButton from "../common/ActionButton";
import { IconButton } from "../common/IconButton";
import {
  formatDate3,
  LinkRecord,
  PolicyAdjustment,
  PolicyProject,
  PolicyProjectType,
} from "winslow-core";
import { ArrowLeft, Folder, FolderOpen, Plus } from "lucide-react";
import { ModalCreatePolicyProject } from "./ModalCreatePolicyProject";
import { ModalCreatePolicyProjectFromLinkRecord } from "./ModalCreatePolicyProjectFromLinkRecord";

import { selectUser } from "../../redux/features/appData";
import { useDispatch, useSelector } from "react-redux";
import { ModalTextInput } from "./ModalTextInput";

import { useNavigate } from "react-router-dom";
import {
  setSelectedMenu,
  setSelectedMenuId,
} from "../../redux/features/appData";
import { MENUS } from "../config/menus";
import { ModalCreatePolicyProjectFromFile } from "./ModalCreatePolicyProjectFromFile";
import ModalConfirm from "./components/ModalConfirm";
import { PolicyStudioZeroState } from "./components/PolicyStudioZeroState";
import { Button } from "../../ds2/components";

export const PolicyStudioSidebar: React.FC<{
  isPublic: boolean;
  policyProjects: PolicyProject[];
  policy: PolicyProject | null;
  isLoading: boolean;
  onSelectPolicy?: (policy: PolicyProject) => void;
  handleUploadFile: (file: File, type: PolicyProjectType) => void;
  handlePolicyCreation: (type: PolicyProjectType) => void;
  handleAdjustmentUpdate: (adjustment: PolicyAdjustment) => void;
  handlePromptUpdate: (prompt: string) => void;
  handlePolicyTitleUpdate: (newTitle: string) => void;
  handleCreateFromLinkRecord: (
    linkRecordId: string,
    type: PolicyProjectType
  ) => void;
  handleDelete: (policyId: string) => void;
  isCreateModalOpen: boolean;
  handleAddNewClick: () => void;
  handleCloseCreateModal: () => void;
  handleCreateFromFileClick: () => void;
  linkRecords: LinkRecord[];
  isCreateFromFileModalOpen: boolean;
  handleCloseCreateFromFileModal: () => void;
  isStreaming: boolean;
}> = ({
  policyProjects,
  policy,
  isLoading,
  onSelectPolicy,
  handlePolicyCreation,
  handleAdjustmentUpdate,
  handlePromptUpdate,
  handlePolicyTitleUpdate,
  handleCreateFromLinkRecord,
  handleDelete,
  handleAddNewClick,
  handleCreateFromFileClick,
  handleUploadFile,
  linkRecords,
  isPublic,
  isCreateModalOpen,
  isCreateFromFileModalOpen,
  handleCloseCreateModal,
  handleCloseCreateFromFileModal,
  isStreaming,
}) => {
  const storeUser = useSelector(selectUser);

  const [viewMode, setViewMode] = useState<"single" | "all">("single");

  const [showEditTitleModal, setShowEditTitleModal] = useState(false);
  const [isCreateFromLinkRecordModalOpen, setIsCreateFromLinkRecordModalOpen] =
    useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const toggleViewMode = () => {
    setViewMode(viewMode === "single" ? "all" : "single");
  };

  const handlePolicySelect = (selectedPolicy: PolicyProject) => {
    if (onSelectPolicy) {
      onSelectPolicy(selectedPolicy);
      setViewMode("single");
    }
  };

  return (
    <div className="policy-sidebar">
      <div className="policy-sidebar-content">
        <div className="policy-sidebar-actions" />

        <div className="policy-sidebar-upload-actions">
          <IconButton
            iconSrc={isPublic ? "./winslow_logo.svg" : undefined}
            iconComponent={isPublic ? undefined : <ArrowLeft size={20} />}
            alt="Back"
            border={false}
            hover={false}
            onClick={() => {
              if (isPublic) {
                window.location.href = "https://usewinslow.com";
              } else {
                dispatch(setSelectedMenu(MENUS.HOME));
                dispatch(setSelectedMenuId(MENUS.HOME));
                navigate("/");
              }
            }}
          />

          {!isPublic && (
            <IconButton
              iconSrc={
                // viewMode === "all"
                //   ? "./icons/policy-studio-single-project.svg"
                //   : "./icons/policy-studio-all-projects.svg"
                undefined
              }
              iconComponent={
                viewMode === "all" ? (
                  <FolderOpen size={20} color="red" />
                ) : (
                  <Folder
                    size={20}
                    color={
                      isPublic || isLoading || policyProjects.length === 0
                        ? "lightgrey"
                        : "black"
                    }
                  />
                )
              }
              border={false}
              disabled={isPublic || isLoading || policyProjects.length === 0}
              alt="All Projects"
              onClick={toggleViewMode}
            />
          )}

          <IconButton
            // iconSrc="./icons/policy-studio-add-new.svg"
            iconComponent={<Plus size={20} />}
            border={true}
            alt="Add New"
            disabled={isLoading}
            onClick={handleAddNewClick}
          />

          <Button
            label="Upload"
            variant="primary"
            disabled={isLoading}
            onClick={handleCreateFromFileClick}
          />

          {!isPublic && (
            <Button
              label="From Resource"
              variant="primary"
              disabled={isPublic || isLoading}
              onClick={() => setIsCreateFromLinkRecordModalOpen(true)}
            />
          )}
        </div>

        {viewMode === "single" ? (
          policy ? (
            <PolicyStudioSidebarSingleProject
              policy={policy}
              handleAdjustmentUpdate={handleAdjustmentUpdate}
              handlePromptUpdate={handlePromptUpdate}
              isLoading={isLoading}
              setShowEditTitleModal={setShowEditTitleModal}
              showEditTitleModal={showEditTitleModal}
              isStreaming={isStreaming}
            />
          ) : (
            <PolicyStudioZeroState isPublic={isPublic} />
          )
        ) : policyProjects.length > 0 ? (
          <PolicyStudioSidebarAllProjects
            policyProjects={new Array(...policyProjects).sort((a, b) => {
              return a.title.localeCompare(b.title);
            })}
            onSelectPolicy={handlePolicySelect}
            isLoading={isLoading}
            onDelete={handleDelete}
          />
        ) : (
          <PolicyStudioZeroState isPublic={isPublic} />
        )}
      </div>
      <ModalCreatePolicyProject
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSubmit={handlePolicyCreation}
      />
      <ModalTextInput
        isOpen={showEditTitleModal}
        onClose={() => setShowEditTitleModal(false)}
        onSubmit={handlePolicyTitleUpdate}
        value={policy?.title || ""}
        title="Edit project name"
        inputPlaceholder="Enter a new title"
        autofocus={true}
      />
      <ModalCreatePolicyProjectFromLinkRecord
        isOpen={isCreateFromLinkRecordModalOpen}
        onClose={() => setIsCreateFromLinkRecordModalOpen(false)}
        onSubmit={handleCreateFromLinkRecord}
        userId={storeUser?.id || ""}
        linkRecords={linkRecords}
      />
      <ModalCreatePolicyProjectFromFile
        isOpen={isCreateFromFileModalOpen}
        onClose={handleCloseCreateFromFileModal}
        onSubmit={handleUploadFile}
      />
    </div>
  );
};

const PolicyStudioSidebarAllProjects: React.FC<{
  policyProjects: PolicyProject[];
  onSelectPolicy: (policy: PolicyProject) => void;
  isLoading: boolean;
  onDelete: (policyId: string) => void;
}> = ({ policyProjects, onSelectPolicy, isLoading, onDelete }) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<string | null>(null);

  const convertContentToShortenedContent = (content: string) => {
    let processedContent = content;

    // Remove ```markdown or ``` from the start if present
    processedContent = processedContent.replace(/^```(?:markdown)?\n?/, "");

    const lines = processedContent.split("\n");
    const contentLines = lines.filter(
      (line) =>
        line.trim() &&
        !line.startsWith("#") &&
        !line.startsWith("-") &&
        !line.startsWith("*") &&
        !line.startsWith("```")
    );

    return contentLines[0] || "";
  };

  const handleDeleteClick = (e: React.MouseEvent, policyId: string) => {
    e.stopPropagation();
    setPolicyToDelete(policyId);
    setShowConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    if (policyToDelete) {
      onDelete(policyToDelete);
      setShowConfirmDelete(false);
      setPolicyToDelete(null);
    }
  };

  return (
    <>
      <div
        className={`policy-sidebar-all-projects ${isLoading ? "blur-content" : ""}`}>
        {policyProjects.map((project, index) => (
          <div
            key={index}
            className="policy-project-item"
            onClick={() => !isLoading && onSelectPolicy(project)}>
            <div className="policy-project-title">
              {project.title}
              <span
                className="policy-project-delete"
                onClick={(e) => handleDeleteClick(e, project.id)}>
                ×
              </span>
            </div>
            <div className="policy-project-content">
              {convertContentToShortenedContent(project.content)}
            </div>
            <div className="policy-project-updated">
              Updated {formatDate3(new Date(project.updatedAt))}
            </div>
          </div>
        ))}
      </div>
      <ModalConfirm
        isOpen={showConfirmDelete}
        title="Delete Policy"
        text="Are you sure you want to delete this policy? This action cannot be undone."
        onClose={() => {
          setShowConfirmDelete(false);
          setPolicyToDelete(null);
        }}
        onContinue={handleConfirmDelete}
      />
    </>
  );
};

const PolicyStudioSidebarSingleProject: React.FC<{
  policy: PolicyProject;
  handleAdjustmentUpdate: (adjustment: PolicyAdjustment) => void;
  handlePromptUpdate: (prompt: string) => void;
  isLoading: boolean;
  showEditTitleModal: boolean;
  setShowEditTitleModal: (show: boolean) => void;
  isStreaming: boolean;
}> = ({
  policy,
  handleAdjustmentUpdate,
  handlePromptUpdate,
  isLoading,
  setShowEditTitleModal,
  isStreaming,
}) => {
  const [message, setMessage] = useState("");

  const handleSend = () => {
    if (message.trim()) {
      handlePromptUpdate(message);
      setMessage("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      style={{ height: "100%" }}
      className={isLoading || isStreaming ? "blur-content" : ""}>
      <div
        className="policy-sidebar-project-header"
        onClick={() => setShowEditTitleModal(true)}>
        <span>{policy.title}</span>
        <img
          src="./icons/policy-studio-edit-title.svg"
          alt=""
          className="policy-sidebar-menu-icon"
          loading="lazy"
        />
      </div>
      <div className="policy-sidebar-content-container">
        <div
          className="policy-sidebar-adjustments-wrapper"
          style={
            policy.adjustments.length === 0
              ? { height: "calc(80vh - 120px)" }
              : {}
          }>
          {policy.adjustments.map((adjustment, index) => (
            <PolicyAdjustmentCard
              key={index}
              adjustment={adjustment}
              onUpdate={handleAdjustmentUpdate}
              disabled={isLoading || isStreaming}
            />
          ))}
          {policy.adjustments.length === 0 && (
            <div className="policy-sidebar-adjustments-zero-state">
              This policy has no templatized adjustments.
            </div>
          )}
        </div>

        <div
          className="policy-sidebar-history-container"
          style={policy.adjustments.length === 0 ? { height: "80vh" } : {}}>
          <div className="policy-sidebar-history-content">
            {policy.history.map((item, index) => (
              <UpdateHistoryItem key={index} history={item} />
            ))}
          </div>
          <div className="policy-sidebar-history-prompt">
            <textarea
              className="policy-sidebar-textarea"
              placeholder="Request a change to the policy..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isLoading || isStreaming}
            />
            <img
              src="./icons/policy-studio-message-send.svg"
              alt=""
              className="policy-sidebar-history-send-icon"
              loading="lazy"
              onClick={!isLoading ? handleSend : undefined}
              style={{
                opacity: isLoading ? 0.5 : 1,
                cursor: isLoading ? "default" : "pointer",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyStudioSidebar;
